import { useMemo } from 'react';
import { NetworkStatus } from '@apollo/client';

import { useGetExternalOffersQuery } from '@ocx/graphql';

import { createExternalOffersQueryVariablesFromCategories } from '../external-loyalty/external-offers.utils';
import { useDisplayCategories, type UseDisplayCategoriesFilter } from '../use-display-categories.hook';
import { groupOfferAndCategories } from './group-offers-display-categories.utils';

import type { Offer } from '../offer.types';

export type UseOffersCarouselCategoriesOptions = {
  persistToStorage?: boolean;
};

const isQueryInFlight = (networkStatus: NetworkStatus) =>
  networkStatus === NetworkStatus.loading ||
  networkStatus === NetworkStatus.setVariables ||
  networkStatus === NetworkStatus.refetch;

export const useOffersCarouselCategories = (
  offers: Offer[],
  filter: UseDisplayCategoriesFilter,
  options: UseOffersCarouselCategoriesOptions = {},
) => {
  const { persistToStorage } = options;
  const {
    data: categories,
    loading: categoriesLoading,
    refetch: refetchCategories,
  } = useDisplayCategories({
    filter,
    persistToStorage,
  });

  const variables = useMemo(() => createExternalOffersQueryVariablesFromCategories(categories), [categories]);
  const skipExternal = !variables.filter.bannerConfigurationUuidIncludes.length;

  const {
    data: externalBanners,
    networkStatus: externalNetworkStatus,
    refetch: refetchEagerExternalOffers,
  } = useGetExternalOffersQuery({
    skip: skipExternal,
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const grouped = useMemo(
    () => groupOfferAndCategories(categories, offers, externalBanners?.banners),
    [offers, categories, externalBanners?.banners],
  );

  const loading = categoriesLoading || (!skipExternal && isQueryInFlight(externalNetworkStatus));

  return {
    categories: grouped,
    loading,
    refetchCategories,
    refetchEagerExternalOffers: skipExternal ? undefined : refetchEagerExternalOffers,
  };
};
